// Axios configuration for Rails integration
import axios from 'axios'

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Get CSRF token from Rails meta tag
function getCSRFToken() {
  const token = document.querySelector('meta[name="csrf-token"]')
  return token ? token.getAttribute('content') : null
}

// Devise renouvelle le jeton CSRF à la connexion et à la déconnexion de l'admin : l'API renvoie
// le nouveau, qu'on réinjecte dans la meta tag pour les requêtes suivantes.
export function setCSRFToken(value) {
  const token = document.querySelector('meta[name="csrf-token"]')
  if (token && value) token.setAttribute('content', value)
}

// Jamais de corps de requête dans la console pour la connexion : il contient le mot de passe.
function loggable(config) {
  return import.meta.env.DEV && !config.url?.startsWith('/admin/session')
}

// Identifiant anonyme de visiteur, envoyé aux démos publiques pour que la limitation de débit
// puisse distinguer deux personnes derrière une même IP — cas courant en entreprise, où tous les
// postes d'un site sortent par la même adresse publique. Ne contient aucune donnée personnelle et
// ne sert qu'à ce comptage ; la limite par IP reste en place pour les cas d'abus.
const CLIENT_ID_KEY = 'demo-client-id'

function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(CLIENT_ID_KEY, id)
    }
    return id
  } catch {
    // localStorage indisponible (navigation privée, cookies bloqués) : seule la limite par IP
    // s'appliquera à ce visiteur.
    return null
  }
}

// Request interceptor - add CSRF token to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Add CSRF token for Rails
    const csrfToken = getCSRFToken()
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken
    }

    const clientId = getClientId()
    if (clientId) {
      config.headers['X-Demo-Client'] = clientId
    }

    if (loggable(config)) {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params)
    }
    return config
  },
  (error) => {
    console.error('❌ Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle common responses
apiClient.interceptors.response.use(
  (response) => {
    if (loggable(response.config)) {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
    }
    return response
  },
  (error) => {
    console.error('❌ Response error:', error.response?.data || error.message)
    
    // Handle common HTTP errors
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Session admin expirée (inactivité, déconnexion ailleurs) : retour à la connexion, en
          // revenant ensuite sur la page en cours.
          if (error.config?.url?.startsWith('/admin/') && !error.config.url.startsWith('/admin/session')) {
            const back = encodeURIComponent(window.location.pathname)
            window.location.href = `/admin/login?redirect=${back}`
          }
          break
        case 403:
          // Forbidden
          console.warn('Access forbidden:', error.response.data)
          break
        case 422:
          // Validation errors (Rails standard)
          console.warn('Validation errors:', error.response.data)
          break
        case 500:
          // Server error
          console.error('Server error:', error.response.data)
          break
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - check your connection')
    }
    
    return Promise.reject(error)
  }
)

// Create a simple client for non-API requests (Rails forms, etc.)
const railsClient = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Accept': 'text/html,application/xhtml+xml'
  }
})

// Add CSRF token to Rails client too
railsClient.interceptors.request.use((config) => {
  const csrfToken = getCSRFToken()
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken
  }
  return config
})

export { apiClient, railsClient }
export default apiClient
